import { useEffect, useRef, useState } from "react";
import { getBallVideoPlayback } from "../../api/recordings.api";
import { loadLocalMatchFile } from "../../hooks/useLocalMatchFile";
import { findChunkForMs } from "../../hooks/useLiveChunks";
import { useLiveMatch } from "../../hooks/useLiveMatch";

// Per-ball video playback used by QC + LiveScorecardModal.
// Operator's ±5s seek lives on the main BBB recorder (VideoPlayer.jsx),
// not here — keep this QC review surface using native <video controls>
// only so the QC scrub experience stays minimal.
//
// Backend returns one of three shapes:
//   type=mp4       → presigned S3 URL, play directly.
//   type=hls_live  → IVS HLS URL; load via hls.js (or Safari native HLS) and
//                    seek to start_ms so the operator sees the just-bowled
//                    delivery, not the live edge.
//   type=processing→ slicer still running; show a small "processing" panel
//                    that re-polls every 5s.
export default function BallVideoPlayer({ ballId, matchId, ball }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [meta, setMeta] = useState(null);   // { type, url, start_ms, end_ms } | null
  const [error, setError] = useState("");
  const [pollTick, setPollTick] = useState(0);
  // Local-file fallback: when matchId + start_ms/end_ms are present AND the
  // operator's pre-recorded file is in IDB, we can play "the clip" by
  // seeking the local <video> to start_ms and stopping at end_ms. This is
  // INSTANT — no upload, no slicer wait — and works for both the BBB
  // operator and a QC tab opened from the same browser.
  const [localFileUrl, setLocalFileUrl] = useState(null);
  const [localFileName, setLocalFileName] = useState("");
  // Live-recording per-chunk playback. When the BBB recorder is capturing
  // right now, every chunk lives in zcd_live_chunks_v1 (one row per
  // timeslice). For QC during a live match we look up the ONE chunk
  // covering the ball's video_start_ms and play it standalone via a
  // Blob URL, seeking into its local timeline.
  //
  // History: we used to try MSE-stitched playback across multiple chunks
  // to support clips that span chunk boundaries. That path threw
  // "Append failed for part 1: decode error" reliably — MediaRecorder
  // emits each timeslice as a SELF-CONTAINED MP4 (its own ftyp + moov
  // + mdat), NOT as a fragmented MP4 sequence of init + media fragments.
  // MSE's appendBuffer is built for the latter, so chunk 2+ always fails
  // to parse, and even chunk 1 fails when the recorder's MIME doesn't
  // exactly match what MSE expects. Single-chunk standalone playback
  // sidesteps the whole class of bugs: each chunk IS a valid MP4 the
  // browser plays directly. Balls that span chunk boundaries fall back
  // to playing the chunk that contains the start frame (the operator
  // sees the beginning of the ball; the durable S3 clip lands after
  // Stop recording fires the slicer).
  const [liveChunkUrl,    setLiveChunkUrl]    = useState(null);
  const [liveChunkStart,  setLiveChunkStart]  = useState(null);
  const [liveChunkEnd,    setLiveChunkEnd]    = useState(null);
  // Try the older single-blob file mirror first (used by "Upload from
  // computer" file-mode workflow — that one IS a valid full MP4).
  useEffect(() => {
    if (!matchId) return;
    let alive = true;
    let createdUrl = null;
    loadLocalMatchFile(matchId).then(rec => {
      if (!alive || !rec?.file_blob) return;
      createdUrl = URL.createObjectURL(rec.file_blob);
      setLocalFileUrl(createdUrl);
      setLocalFileName(rec.file_name || "");
    });
    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [matchId]);
  // Then try the per-chunk live-recording store. This is the fast
  // path during a session in the same browser tab/window.
  const ballStartMs = ball?.video?.video_start_ms ?? ball?.video_start_ms ?? null;
  const ballEndMs   = ball?.video?.video_end_ms   ?? ball?.video_end_ms   ?? null;
  useEffect(() => {
    if (!matchId || ballStartMs == null || ballEndMs == null) return;
    let alive = true;
    let createdUrl = null;
    findChunkForMs(matchId, ballStartMs).then((row) => {
      if (!alive || !row) return;
      createdUrl = URL.createObjectURL(row.blob);
      // Map the ball's master-timeline range into the chunk's local
      // timeline (chunk starts at offset 0 in its own MP4). Clamp the
      // end at the chunk's duration so a ball that overruns the chunk
      // boundary still plays the visible portion — beats refusing to
      // play at all.
      const inChunkStart = Math.max(0, ballStartMs - row.start_offset_ms);
      const inChunkEnd   = Math.min(ballEndMs - row.start_offset_ms, row.chunk_ms);
      setLiveChunkUrl(createdUrl);
      setLiveChunkStart(inChunkStart);
      setLiveChunkEnd(inChunkEnd);
    });
    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setLiveChunkUrl(null);
    };
  }, [matchId, ballStartMs, ballEndMs]);

  // Fetch playback info — re-fetches when ballId changes OR when we're
  // in `processing` and the 5s tick fires.
  useEffect(() => {
    let alive = true;
    setError("");
    getBallVideoPlayback(ballId)
      .then((d) => { if (alive) setMeta(d); })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 404) setMeta({ type: "none" });
        else setError(e?.response?.data?.detail || e.message || "load failed");
      });
    return () => { alive = false; };
  }, [ballId, pollTick]);

  // Event-driven refresh: the backend MediaConvert webhook broadcasts a
  // `ball_clip_ready` event on the match WS group the moment a clip is
  // ready. Listen for it and refetch playback exactly once instead of
  // polling every 5 seconds. A safety-net poll fires every 60s while in
  // `processing` so a missed WS event (late tab open, network blip)
  // still recovers — much lower load than the old 5s loop.
  const { latest } = useLiveMatch({
    matchId,
    autoConnect: !!matchId && meta?.type === "processing",
  });
  useEffect(() => {
    if (!latest) return;
    if (
      (latest.type === "ball_clip_ready" || latest.type === "ball_clip_failed")
      && String(latest.ball_id) === String(ballId)
    ) {
      setPollTick((t) => t + 1);
    }
  }, [latest, ballId]);
  useEffect(() => {
    if (meta?.type !== "processing") return undefined;
    const id = setTimeout(() => setPollTick((t) => t + 1), 60000);
    return () => clearTimeout(id);
  }, [meta]);

  // Hook up the actual playback once meta is in.
  useEffect(() => {
    if (!videoRef.current || !meta?.url) return undefined;
    const video = videoRef.current;
    let cancelled = false;

    const seekToStart = () => {
      if (meta.start_ms != null) {
        try { video.currentTime = meta.start_ms / 1000; } catch { /* ignore */ }
      }
    };

    if (meta.type === "mp4") {
      video.src = meta.url;
      video.onloadedmetadata = seekToStart;
      return () => { video.src = ""; };
    }

    if (meta.type === "hls_live") {
      // Safari can play .m3u8 natively. Everyone else needs hls.js.
      const native = video.canPlayType("application/vnd.apple.mpegurl");
      if (native) {
        video.src = meta.url;
        video.onloadedmetadata = seekToStart;
        return () => { video.src = ""; };
      }
      (async () => {
        const Hls = (await import("hls.js")).default;
        if (cancelled) return;
        if (!Hls.isSupported()) {
          setError("HLS not supported in this browser");
          return;
        }
        const hls = new Hls({ liveSyncDurationCount: 3 });
        hls.loadSource(meta.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, seekToStart);
        hlsRef.current = hls;
      })();
      return () => {
        cancelled = true;
        if (hlsRef.current) {
          try { hlsRef.current.destroy(); } catch { /* ignore */ }
          hlsRef.current = null;
        }
      };
    }
    return undefined;
  }, [meta]);

  // Playback path picker. Preference order:
  //   1. S3 mp4 — final, durable clip from the slicer.
  //   2. Live single-chunk — in-session per-chunk Blob URL.
  //   3. Single-file local blob — file-mode upload review.
  const localStartMs = ballStartMs;
  const localEndMs = ballEndMs;
  const haveLocal = !!localFileUrl && localStartMs != null && localEndMs != null;
  const haveLiveChunk = !!liveChunkUrl && liveChunkStart != null;
  const s3Ready = meta?.type === "mp4" || meta?.type === "hls_live";

  const anyLocal = haveLocal || haveLiveChunk;
  if (error && !anyLocal) {
    return <div className="text-xs text-rose-400">Couldn't load video: {error}</div>;
  }
  if (!meta && !anyLocal) {
    return <div className="text-xs text-text-secondary">Loading video…</div>;
  }
  if (meta?.type === "none" && !anyLocal) {
    return (
      <div className="text-xs text-text-secondary italic">
        No clip — ball was saved without Clip in/out marked, or before
        recording started.
      </div>
    );
  }
  if (meta?.type === "processing" && !anyLocal) {
    return (
      <div className="text-xs text-amber-300 flex items-start gap-2 leading-snug">
        <span className="w-2 h-2 mt-1 rounded-full bg-amber-300 animate-pulse shrink-0" />
        <span>
          Recording still in progress — clip will appear here once you
          click <strong className="text-amber-200">Stop recording</strong> on the
          BBB page (master video has to finalise before the slicer can
          cut per-ball clips).
        </span>
      </div>
    );
  }

  // Live single-chunk playback (same-session recording). Plays the
  // MediaRecorder chunk that contains the ball's start frame; the
  // chunk is a valid standalone MP4 so the browser decodes it
  // directly without MSE. Durable S3 clips land after the operator
  // hits Stop recording — the slicer takes over from there.
  if (haveLiveChunk && !s3Ready) {
    return (
      <LocalClipPlayer
        url={liveChunkUrl}
        startMs={liveChunkStart}
        endMs={liveChunkEnd}
        fileName="(live)"
      />
    );
  }

  // File-mode local fallback (single-blob upload review).
  if (haveLocal && !s3Ready) {
    return (
      <LocalClipPlayer
        url={localFileUrl}
        startMs={localStartMs}
        endMs={localEndMs}
        fileName={localFileName}
      />
    );
  }

  return (
    <div className="space-y-1">
      <video
        ref={videoRef}
        controls
        // defaultMuted (not `muted`) — initial-only, lets the operator's
        // unmute click via native controls stick across renders.
        defaultMuted
        preload="metadata"
        className="w-full max-w-md rounded border border-bg-secondary"
      />
      {meta.type === "hls_live" && (
        <div className="text-[10px] uppercase tracking-wider text-rose-300 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
          Live · seeking to ball start
        </div>
      )}
    </div>
  );
}

// Plays the operator's locally-stored match file scoped to a ms range.
// Used for instant per-ball preview before the S3 upload + slicer have
// produced the real per-ball clip.
function LocalClipPlayer({ url, startMs, endMs, fileName }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const seek = () => {
      try { v.currentTime = startMs / 1000; } catch { /* ignore */ }
    };
    const enforceEnd = () => {
      if (v.currentTime * 1000 >= endMs) {
        v.pause();
        try { v.currentTime = endMs / 1000; } catch { /* ignore */ }
      }
    };
    v.addEventListener("loadedmetadata", seek);
    v.addEventListener("timeupdate", enforceEnd);
    return () => {
      v.removeEventListener("loadedmetadata", seek);
      v.removeEventListener("timeupdate", enforceEnd);
    };
  }, [startMs, endMs]);
  return (
    <div className="space-y-1">
      <video
        ref={videoRef}
        src={url}
        controls
        defaultMuted
        preload="metadata"
        className="w-full max-w-md rounded border border-bg-secondary"
      />
      <div className="text-[10px] uppercase tracking-wider text-violet-300 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
        Local preview · {fileName ? `${fileName} · ` : ""}{((endMs - startMs) / 1000).toFixed(1)}s
      </div>
    </div>
  );
}
